import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import DocumentTemplateApi, { loadModalTemplatePageInput } from '../api/DocumentTemplate.api';
import type { GenerationOptions } from '../pages/DocumentEditorPage/documentEditorOptions';

export type LoadableTemplate = {
  name: string;
  entityType: string;
  templateState: string;
  type: string;
  id: number | string;
  isTemplate?: boolean;
  showInBulkPrintMenu?: boolean;
  generationOptions?: GenerationOptions | null;
};

export type TemplatePage = {
  documentTemplatePage: {
    edges?: Array<{ node: LoadableTemplate } | null> | null;
    pageInfo?: { hasNextPage?: boolean | null; endCursor?: string | null } | null;
  };
};

/**
 * The document editor load modal's read: one page of the org's templates per search term, network first on every
 * open, then from the cache; `fetchMore` appends the next page.
 */
export function useDocumentTemplateLoadPage(term: string) {
  const {
    data,
    fetchMore: fetchMorePages,
    loading,
    error,
  } = useQuery<TemplatePage>(DocumentTemplateApi.GetDocumentTemplatesState, {
    variables: { input: loadModalTemplatePageInput(term) },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const templates = useMemo(
    () => (data?.documentTemplatePage.edges ?? []).flatMap((edge) => (edge ? [edge.node] : [])),
    [data]
  );
  const pageInfo = data?.documentTemplatePage.pageInfo;
  const endCursor = pageInfo?.hasNextPage ? pageInfo.endCursor : null;

  const fetchMore = async () => {
    if (!endCursor) return;
    await fetchMorePages({
      variables: { input: loadModalTemplatePageInput(term, endCursor) },
      updateQuery: (previous, { fetchMoreResult }) => ({
        documentTemplatePage: {
          ...fetchMoreResult.documentTemplatePage,
          edges: [
            ...(previous.documentTemplatePage.edges ?? []),
            ...(fetchMoreResult.documentTemplatePage.edges ?? []),
          ],
        },
      }),
    });
  };

  return { templates, hasNextPage: Boolean(endCursor), fetchMore, loading, error };
}
